import {useQuery} from '@tanstack/react-query';
import {kratosAuth} from '../api';
import {AxiosError} from 'axios';
import {schemaIdToRole, AppRole} from '../model';

interface SessionData {
  identity: {
    id: string;
    schema_id?: string;
    metadata_public?: {
      fund_id?: string;
      [key: string]: unknown;
    };
  };
  active?: boolean;
}

export const useSession = () => {
  const queryInfo = useQuery<SessionData, AxiosError>({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        const response = await kratosAuth.checkSession();
        if (response.data && (response.data as any).active !== false) {
          return response.data;
        } else {
          const error = new Error('Session is not active or not found') as AxiosError;
          error.response = {status: 401, data: {}, statusText: 'Unauthorized', headers: {}, config: {} as any};
          throw error;
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          if (error.response?.status === 401 || error.response?.status === 403) {
            throw error;
          }
        }
        throw error;
      }
    },
    retry: (failureCount, error) => {
      if (error instanceof AxiosError && (error.response?.status === 401 || error.response?.status === 403)) {
        return false;
      }
      return failureCount < 3;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const schemaId = queryInfo.data?.identity?.schema_id;
  const fundId = queryInfo.data?.identity?.metadata_public?.fund_id;
  const role: AppRole = schemaIdToRole(schemaId);

  return {
    sessionData: queryInfo.data,
    isActive: !!queryInfo.data?.identity?.id && queryInfo.data?.active !== false,
    isLoading: queryInfo.isLoading,
    isFetching: queryInfo.isFetching,
    isError: queryInfo.isError,
    error: queryInfo.error,
    schemaId,
    fundId,
    role,
  };
};
